    import React, { useState, useRef, useEffect } from 'react';
    import { FaFileImage, FaFilePdf, FaEye, FaClipboard, FaFileWord, FaEllipsisV, FaUpload, FaRobot, FaQuestionCircle, FaTimes, FaExpand, FaChevronLeft, FaChevronRight, FaDownload, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaChartBar, FaUser, FaGraduationCap, FaBriefcase, FaCogs } from 'react-icons/fa';

    const ResumeAnalysis = () => {
        const fileInputRef = useRef();
        const [progress, setProgress] = useState(0);
        const [extractedText, setExtractedText] = useState('');
        const [analysisResults, setAnalysisResults] = useState(null);
        const [additionalAnalysis, setAdditionalAnalysis] = useState('');
        const [qualifications, setQualifications] = useState('');
        const [userPrompt, setUserPrompt] = useState('');
        const [loading, setLoading] = useState(false);
        const [isLoaded, setIsLoaded] = useState(false);
        const [fileName, setFileName] = useState('');
        const [uploadedImages, setUploadedImages] = useState([]);
        const [ocrProgress, setOcrProgress] = useState('');
        const [gptResults, setGptResults] = useState('');

        // Modal states for image preview
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [currentImageIndex, setCurrentImageIndex] = useState(0);

        const [qualificationMetricsDropdownOpen, setQualificationMetricsDropdownOpen] = useState(false);
        const [analysisDropdownOpen, setAnalysisDropdownOpen] = useState(false);
        const [additionalDropdownOpen, setAdditionalDropdownOpen] = useState(false);
        const [fullscreenSection, setFullscreenSection] = useState(null); // 'metrics', 'analysis', or 'additional'
        const qualificationMetricsDropdownRef = useRef(null);
        const analysisDropdownRef = useRef(null);
        const additionalDropdownRef = useRef(null);

        const openFullscreen = (section) => {
            setFullscreenSection(section);
        };

        const closeFullscreen = () => {
            setFullscreenSection(null);
        };

        

        // Handle escape key for fullscreen
        useEffect(() => {
            const handleEscapeKey = (e) => {
                if (e.key === 'Escape' && fullscreenSection) {
                    closeFullscreen();
                }
            };

            document.addEventListener('keydown', handleEscapeKey);
            return () => document.removeEventListener('keydown', handleEscapeKey);
        }, [fullscreenSection]);

        // Mock toast function since we don't have the actual library in this environment
        const toast = {
            success: (title, options) => console.log('Success:', title, options?.description),
            error: (title, options) => console.log('Error:', title, options?.description)
        };

        // Mock qualification metrics data
        const [qualificationMetrics, setQualificationMetrics] = useState(null);

        useEffect(() => {
            setIsLoaded(false);
            const timer = setTimeout(() => {
                setIsLoaded(true);
            }, 50);
            return () => clearTimeout(timer);
        }, []);

        

        // Initialize with demo metrics on component load
        useEffect(() => {
            // Show demo metrics when component loads
            generateQualificationMetrics();
        }, [qualifications, extractedText]);

        useEffect(() => {
            function handleClickOutside(event) {
                if (qualificationMetricsDropdownRef.current && !qualificationMetricsDropdownRef.current.contains(event.target)) {
                    setQualificationMetricsDropdownOpen(false);
                }
                if (analysisDropdownRef.current && !analysisDropdownRef.current.contains(event.target)) {
                    setAnalysisDropdownOpen(false);
                }
                if (additionalDropdownRef.current && !additionalDropdownRef.current.contains(event.target)) {
                    setAdditionalDropdownOpen(false);
                }
            }
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, []);

        // Load external scripts dynamically
        useEffect(() => {
            const loadScript = (src, name) => {
                return new Promise((resolve, reject) => {
                    if (window[name]) {
                        resolve();
                        return;
                    }
                    const script = document.createElement('script');
                    script.src = src;
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error(`Failed to load ${name}`));
                    document.head.appendChild(script);
                });
            };

            const loadScripts = async () => {
                try {
                    await loadScript('https://cdn.jsdelivr.net/npm/tesseract.js@2/dist/tesseract.min.js', 'Tesseract');
                    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.14.305/pdf.min.js', 'pdfjsLib');

                    if (window.pdfjsLib) {
                        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.14.305/pdf.worker.min.js';
                    }
                } catch (error) {
                    console.error('Error loading external scripts:', error);
                }
            };

            loadScripts();
        }, []);

        // Handle keyboard navigation in modal
        useEffect(() => {
            const handleKeyPress = (e) => {
                if (!isModalOpen) return;

                if (e.key === 'Escape') {
                    closeImageModal();
                } else if (e.key === 'ArrowRight') {
                    nextImage();
                } else if (e.key === 'ArrowLeft') {
                    prevImage();
                }
            };

            document.addEventListener('keydown', handleKeyPress);
            return () => document.removeEventListener('keydown', handleKeyPress);
        }, [isModalOpen, uploadedImages.length]);

        const handleFileChange = (e) => {
            const file = e.target.files[0];
            if (file) {
                setFileName(file.name);
            }
        };

        const processImage = (file) => {
            const imgUrl = URL.createObjectURL(file);
            setUploadedImages([imgUrl]);
            performOCR(file);
        };

        const processPDF = async (file) => {
            if (!window.pdfjsLib) {
                toast.error('PDF Processing Error', {
                    description: 'PDF.js library not loaded yet. Please try again in a moment.',
                    duration: 3000
                });
                return;
            }

            try {
                const pdf = await window.pdfjsLib.getDocument(URL.createObjectURL(file)).promise;
                const images = [];
                let allText = "";

                for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                    const page = await pdf.getPage(pageNum);
                    const viewport = page.getViewport({ scale: 1.5 });
                    const canvas = document.createElement("canvas");
                    const context = canvas.getContext("2d");

                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    await page.render({ canvasContext: context, viewport: viewport }).promise;
                    const dataUrl = canvas.toDataURL("image/png");
                    images.push(dataUrl);
                }

                setUploadedImages(images);

                // Process OCR for all pages
                for (let i = 0; i < images.length; i++) {
                    setOcrProgress(`Processing page ${i + 1} of ${images.length}...`);
                    const text = await performOCRAsync(images[i]);
                    allText += text + "\n";
                    setProgress(Math.round(((i + 1) / images.length) * 70)); // 70% for OCR
                }

                setExtractedText(allText);
                await sendToGPT(allText);

            } catch (error) {
                console.error("Error processing PDF:", error);
                toast.error('PDF Processing Failed', {
                    description: 'An error occurred while processing the PDF file.',
                    duration: 3000
                });
                setLoading(false);
            }
        };

        const performOCR = (imageData) => {
            if (!window.Tesseract) {
                console.error("Tesseract not loaded");
                return;
            }

            setOcrProgress("Starting OCR processing...");

            window.Tesseract.recognize(imageData, "eng", {
                logger: (m) => {
                    if (m.status === 'recognizing text') {
                        const ocrProgress = Math.round(m.progress * 70); // 70% for OCR
                        setProgress(ocrProgress);
                        setOcrProgress(`OCR Progress: ${Math.round(m.progress * 100)}%`);
                    }
                }
            })
                .then(({ data: { text } }) => {
                    setExtractedText(text);
                    setOcrProgress("OCR completed! Analyzing with AI...");
                    sendToGPT(text);
                })
                .catch((error) => {
                    console.error("Error during OCR processing:", error);
                    toast.error('OCR Processing Failed', {
                        description: 'An error occurred during text extraction.',
                        duration: 3000
                    });
                    setLoading(false);
                });
        };

        // Helper function for async OCR processing (for PDFs)
        const performOCRAsync = (imageData) => {
            return new Promise((resolve, reject) => {
                if (!window.Tesseract) {
                    reject(new Error("Tesseract not loaded"));
                    return;
                }

                window.Tesseract.recognize(imageData, "eng", {
                    logger: (m) => {
                        if (m.status === 'recognizing text') {
                            setOcrProgress(`OCR Progress: ${Math.round(m.progress * 100)}%`);
                        }
                    }
                })
                    .then(({ data: { text } }) => {
                        resolve(text);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        };

        const sendToGPT = async (text) => {
            try {
                setProgress(75); // 75% when starting GPT analysis
                setOcrProgress("Analyzing resume with AI...");

                // Generate qualification metrics first
                generateQualificationMetrics(text);

                const response = await fetch('http://localhost:5000/api/resume/gpt-analysis', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        extractedText: text,
                        qualifications: qualifications,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`GPT API error: ${response.status}`);
                }

                const responseData = await response.json();
                displayGPTResponse(responseData.analysis);
                setGptResults(responseData.analysis);

                setProgress(100);

                toast.success('Resume Analysis Complete', {
                    description: `Resume "${fileName}" has been successfully analyzed.`,
                    duration: 5000
                });

            } catch (error) {
                console.error("Error during GPT processing:", error);
                toast.error('AI Analysis Failed', {
                    description: 'An error occurred during the AI analysis process.',
                    duration: 3000
                });

                // Still generate qualification metrics even if API fails
                generateQualificationMetrics(text);
            } finally {
                setLoading(false);
                setOcrProgress("");
            }
        };

        // Generate dynamic qualification metrics based on job description and extracted text
        const generateQualificationMetrics = (resumeText = null) => {
            const textToAnalyze = resumeText || extractedText;

            if (!qualifications.trim() || !textToAnalyze.trim()) {
                // Create a simple demo metrics if no data is available
                const demoMetrics = {
                    overallFit: 0,
                    requiredSkills: [],
                    experience: {
                        required: "Not specified",
                        candidate: "Not analyzed",
                        match: false
                    },
                    education: {
                        required: "Not specified",
                        candidate: "Not analyzed",
                        match: false
                    },
                    recommendations: ["Please upload a resume and enter job requirements to see detailed metrics"]
                };
                setQualificationMetrics(demoMetrics);
                return;
            }

            // Parse job description for requirements
            const jobDesc = qualifications.toLowerCase();
            const resume = textToAnalyze.toLowerCase();

            // Extract skills from job description
            const skillKeywords = [
                'javascript', 'js', 'react', 'angular', 'vue', 'node.js', 'nodejs', 'python', 'java', 'c++', 'c#',
                'php', 'ruby', 'go', 'rust', 'typescript', 'html', 'css', 'sql', 'mongodb', 'postgresql',
                'mysql', 'redis', 'docker', 'kubernetes', 'aws', 'azure', 'gcp', 'git', 'jenkins', 'ci/cd',
                'agile', 'scrum', 'rest', 'api', 'graphql', 'microservices', 'devops', 'linux', 'windows',
                'machine learning', 'ai', 'data science', 'pandas', 'numpy', 'tensorflow', 'pytorch',
                'excel', 'powerbi', 'tableau', 'photoshop', 'illustrator', 'figma', 'sketch', 'ui/ux',
                'project management', 'leadership', 'communication', 'teamwork', 'problem solving'
            ];

            // Find skills mentioned in job description
            const requiredSkills = skillKeywords.filter(skill =>
                jobDesc.includes(skill) || jobDesc.includes(skill.replace(/[.\-]/g, ' '))
            );

            // Check which skills candidate has
            const candidateSkills = skillKeywords.filter(skill =>
                resume.includes(skill) || resume.includes(skill.replace(/[.\-]/g, ' '))
            );

            // Create skills assessment
            const skillsAssessment = requiredSkills.map(skill => {
                const hasSkill = candidateSkills.includes(skill);
                return {
                    skill: skill.charAt(0).toUpperCase() + skill.slice(1),
                    required: true,
                    candidate: hasSkill,
                    match: hasSkill
                };
            });

            // Add additional candidate skills not required
            const additionalSkills = candidateSkills
                .filter(skill => !requiredSkills.includes(skill))
                .slice(0, 3) // Limit to 3 additional skills
                .map(skill => ({
                    skill: skill.charAt(0).toUpperCase() + skill.slice(1),
                    required: false,
                    candidate: true,
                    match: true
                }));

            const allSkills = [...skillsAssessment, ...additionalSkills];

            // Calculate overall fit score
            const requiredSkillsCount = skillsAssessment.length;
            const matchedRequiredSkills = skillsAssessment.filter(s => s.match).length;
            const skillScore = requiredSkillsCount > 0 ? (matchedRequiredSkills / requiredSkillsCount) * 100 : 50;

            // Extract experience requirements
            const experienceMatch = jobDesc.match(/(\d+)[\+\-\s]*(years?|yrs?)/i);
            const requiredExperience = experienceMatch ? experienceMatch[0] : "Experience required";

            // Look for experience in resume
            const resumeExpMatch = resume.match(/(\d+)[\+\-\s]*(years?|yrs?)/i);
            const candidateExperience = resumeExpMatch ? resumeExpMatch[0] : "Experience mentioned";

            // Simple experience matching logic
            const reqExpNum = experienceMatch ? parseInt(experienceMatch[1]) : 0;
            const candExpNum = resumeExpMatch ? parseInt(resumeExpMatch[1]) : 0;
            const experienceMatches = candExpNum >= reqExpNum;

            // Extract education requirements
            const educationKeywords = ['bachelor', 'master', 'phd', 'degree', 'diploma', 'certification'];
            const jobEducation = educationKeywords.find(edu => jobDesc.includes(edu));
            const resumeEducation = educationKeywords.find(edu => resume.includes(edu));
            const educationMatches = jobEducation ? (resumeEducation !== undefined) : true;

            // Generate recommendations based on analysis
            const recommendations = [];

            if (matchedRequiredSkills === requiredSkillsCount && requiredSkillsCount > 0) {
                recommendations.push("✅ Candidate meets all required technical skills");
            } else if (matchedRequiredSkills > requiredSkillsCount * 0.7) {
                recommendations.push("⚠️ Candidate meets most required skills with minor gaps");
            } else if (requiredSkillsCount > 0) {
                recommendations.push("❌ Significant skill gaps identified in required areas");
            } else {
                recommendations.push("ℹ️ No specific technical skills identified in job description");
            }

            if (experienceMatches) {
                recommendations.push("✅ Experience level meets job requirements");
            } else {
                recommendations.push("⚠️ Experience level may be below requirements");
            }

            if (educationMatches) {
                recommendations.push("✅ Educational background aligns with requirements");
            } else {
                recommendations.push("⚠️ Educational background may not match requirements");
            }

            if (additionalSkills.length > 0) {
                recommendations.push(`💡 Candidate brings additional skills: ${additionalSkills.map(s => s.skill).join(', ')}`);
            }

            // Calculate final score considering multiple factors
            const educationScore = educationMatches ? 100 : 50;
            const experienceScore = experienceMatches ? 100 : (candExpNum / reqExpNum) * 100 || 50;
            const overallFit = Math.round((skillScore * 0.5 + experienceScore * 0.3 + educationScore * 0.2));

            const metrics = {
                overallFit: Math.min(100, Math.max(0, overallFit)),
                requiredSkills: allSkills,
                experience: {
                    required: requiredExperience,
                    candidate: candidateExperience,
                    match: experienceMatches
                },
                education: {
                    required: jobEducation ? jobEducation.charAt(0).toUpperCase() + jobEducation.slice(1) : "Not specified",
                    candidate: resumeEducation ? resumeEducation.charAt(0).toUpperCase() + resumeEducation.slice(1) : "Not specified",
                    match: educationMatches
                },
                recommendations: recommendations.slice(0, 4) // Limit to 4 recommendations
            };

            setQualificationMetrics(metrics);
        };

        // ENHANCED: Updated extractDataFromGPTResponse function to handle detailed analysis
        const extractDataFromGPTResponse = (response) => {
            let personalInfo = "";
            let education = "";
            let workExperience = "";
            let detailedAnalysis = "";
            let percentageFit = "";

            const personalInfoMatch = response.match(/\*\*Personal Information\*\*:(.*?)(?=\*\*Educational Attainment|\*\*Work Experience|\*\*Detailed Fit Analysis|\*\*Percentage Fit|$)/is);
            if (personalInfoMatch) {
                personalInfo = personalInfoMatch[1].trim();
            }

            const educationMatch = response.match(/\*\*Educational Attainment\*\*:(.*?)(?=\*\*Personal Information|\*\*Work Experience|\*\*Detailed Fit Analysis|\*\*Percentage Fit|$)/is);
            if (educationMatch) {
                education = educationMatch[1].trim();
            }

            const workExperienceMatch = response.match(/\*\*Work Experience\*\*:(.*?)(?=\*\*Personal Information|\*\*Educational Attainment|\*\*Detailed Fit Analysis|\*\*Percentage Fit|$)/is);
            if (workExperienceMatch) {
                workExperience = workExperienceMatch[1].trim();
            }

            // NEW: Extract the detailed fit analysis section
            const detailedAnalysisMatch = response.match(/\*\*Detailed Fit Analysis\*\*:(.*?)(?=\*\*Percentage Fit|$)/is);
            if (detailedAnalysisMatch) {
                detailedAnalysis = detailedAnalysisMatch[1].trim();
            }

            const percentageFitMatch = response.match(/\*\*Percentage Fit\*\*:(.*?)(?=\*\*|$)/is);
            if (percentageFitMatch) {
                percentageFit = percentageFitMatch[1].trim();
            }

            return {
                personalInfo: personalInfo || "No data found",
                education: education || "No data found",
                workExperience: workExperience || "No data found",
                detailedAnalysis: detailedAnalysis || "No detailed analysis available", // NEW FIELD
                percentageFit: percentageFit || "No data found"
            };
        };

        const displayGPTResponse = (response) => {
            const extractedData = extractDataFromGPTResponse(response);
            setAnalysisResults(extractedData);
        };

        const handleAnalyze = async () => {
            const file = fileInputRef.current.files[0];
            if (!file) {
                toast.error('No File Selected', {
                    description: 'Please select a resume file to analyze.',
                    duration: 3000
                });
                return;
            }

            if (!qualifications.trim()) {
                toast.error('Missing Qualifications', {
                    description: 'Please enter job qualifications before analyzing.',
                    duration: 3000
                });
                return;
            }

            // Reset previous results
            setExtractedText("");
            setAnalysisResults(null);
            setAdditionalAnalysis("");
            setQualificationMetrics(null);
            setProgress(0);
            setLoading(true);

            const fileType = file.type;
            if (fileType === "application/pdf") {
                await processPDF(file);
            } else if (fileType.startsWith("image/")) {
                await processImage(file);
            } else {
                toast.error('Invalid File Type', {
                    description: 'Please upload an image or PDF file.',
                    duration: 3000
                });
                setLoading(false);
            }
        };

        const sendAdditionalPrompt = async () => {
            if (!userPrompt.trim()) {
                toast.error('Empty Prompt', {
                    description: 'Please enter a question before sending.',
                    duration: 3000
                });
                return;
            }

            setLoading(true);

            try {
                const response = await fetch('http://localhost:5000/api/resume/additional-analysis', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        extractedText: extractedText,
                        previousAnalysis: gptResults,
                        userPrompt: userPrompt,
                        qualifications: qualifications,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`Additional analysis API error: ${response.status}`);
                }

                const responseData = await response.json();
                setAdditionalAnalysis(responseData.analysis);
                setUserPrompt(""); // Clear the prompt after sending

                toast.success('Additional Analysis Complete', {
                    description: 'Your question has been analyzed successfully.',
                    duration: 3000
                });

            } catch (error) {
                console.error("Error during additional analysis:", error);
                toast.error('Additional Analysis Failed', {
                    description: 'An error occurred during additional analysis.',
                    duration: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        const copyToClipboard = (text, type) => {
            if (!text || text.trim() === '') {
                toast.error('Copy Failed', {
                    description: 'No content available to copy.',
                    duration: 3000
                });
                return;
            }

            navigator.clipboard.writeText(text).then(
                () => {
                    toast.success(`${type} Copied`, {
                        description: `${type} has been copied to clipboard.`,
                        duration: 3000
                    });
                },
                (err) => {
                    console.error('Could not copy text: ', err);
                    toast.error('Copy Failed', {
                        description: 'An error occurred while copying to clipboard.',
                        duration: 3000
                    });
                }
            );
        };

        const downloadAsWord = (content, filename) => {
            if (!content || content.trim() === '') {
                toast.error('Download Failed', {
                    description: 'No content available to download.',
                    duration: 3000
                });
                return;
            }

            try {
                const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
            <meta charset="UTF-8">
            <title>${filename}</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                pre { white-space: pre-wrap; font-family: Arial, sans-serif; }
            </style>
            </head>
            <body>
            <pre>${content}</pre>
            </body>
            </html>
        `;

                const blob = new Blob([htmlContent], { type: 'application/msword' });
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.download = `${filename}.doc`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                toast.success(`${filename} Downloaded`, {
                    description: `${filename} has been downloaded as a Word document.`,
                    duration: 3000
                });
            } catch (error) {
                console.error('Error downloading as Word:', error);
                toast.error('Download Failed', {
                    description: 'An error occurred while generating the Word document.',
                    duration: 3000
                });
            }
        };

        const downloadAsPDF = (content, filename) => {
            if (!content || content.trim() === '') {
                toast.error('Download Failed', {
                    description: 'No content available to download.',
                    duration: 3000
                });
                return;
            }

            try {
                // Create a properly formatted HTML document for PDF conversion
                const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
            <meta charset="UTF-8">
            <title>${filename}</title>
            <style>
                @page {
                margin: 20mm;
                size: A4;
                }
                body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
                max-width: 100%;
                margin: 0;
                padding: 20px;
                }
                h1 {
                color: #2563eb;
                border-bottom: 2px solid #2563eb;
                padding-bottom: 10px;
                margin-bottom: 20px;
                }
                h2 {
                color: #1f2937;
                margin-top: 25px;
                margin-bottom: 15px;
                }
                .score {
                font-size: 24px;
                font-weight: bold;
                color: #059669;
                background: #f0fdf4;
                padding: 10px;
                border-radius: 8px;
                text-align: center;
                margin: 15px 0;
                }
                .section {
                margin-bottom: 20px;
                padding: 15px;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                }
                .skill-item {
                display: flex;
                justify-content: space-between;
                padding: 5px 0;
                border-bottom: 1px solid #f3f4f6;
                }
                .match { color: #059669; }
                .no-match { color: #dc2626; }
                ul {
                list-style-type: none;
                padding-left: 0;
                }
                li {
                padding: 5px 0;
                margin: 5px 0;
                }
                .recommendation {
                background: #fef3c7;
                padding: 10px;
                margin: 5px 0;
                border-radius: 6px;
                border-left: 4px solid #f59e0b;
                }
            </style>
            </head>
            <body>
            ${formatContentForPDF(content)}
            </body>
            </html>
        `;

                // Create blob with proper PDF mime type
                const blob = new Blob([htmlContent], { type: 'text/html' });
                const url = URL.createObjectURL(blob);

                // Create a temporary iframe for printing
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = url;

                document.body.appendChild(iframe);

                iframe.onload = function () {
                    // Trigger print dialog which can save as PDF
                    iframe.contentWindow.print();

                    // Clean up after a delay
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                        URL.revokeObjectURL(url);
                    }, 1000);
                };

                toast.success(`${filename} PDF Ready`, {
                    description: `Please use your browser's print dialog to save as PDF.`,
                    duration: 5000
                });

            } catch (err) {
                console.error('Error generating PDF:', err);
                toast.error('PDF Generation Failed', {
                    description: 'An error occurred while generating the PDF.',
                    duration: 3000
                });
            }
        };

        // Helper function to format content for PDF
        const formatContentForPDF = (content) => {
            // Parse the qualification metrics content and format as HTML
            const lines = content.split('\n');
            let html = '';
            let currentSection = '';

            for (let line of lines) {
                line = line.trim();
                if (!line) continue;

                if (line.includes('Job Qualification Assessment Report')) {
                    html += `<h1>${line}</h1>`;
                } else if (line.includes('Overall Fit Score:')) {
                    const score = line.split(':')[1].trim();
                    html += `<div class="score">Overall Fit Score: ${score}</div>`;
                } else if (line.includes('Skills Assessment:') ||
                    line.includes('Experience:') ||
                    line.includes('Education:') ||
                    line.includes('Recommendations:')) {
                    if (currentSection) html += '</div>'; // Close previous section
                    html += `<div class="section"><h2>${line}</h2>`;
                    currentSection = line;
                } else if (line.includes('Required:') || line.includes('Candidate:') || line.includes('Match:')) {
                    html += `<p><strong>${line}</strong></p>`;
                } else if (line.match(/^\d+\./)) {
                    html += `<div class="recommendation">${line}</div>`;
                } else if (line.includes(':')) {
                    const [skill, status] = line.split(':');
                    const className = status.includes('MATCH') ? 'match' : 'no-match';
                    html += `<div class="skill-item">
            <span>${skill}</span>
            <span class="${className}">${status}</span>
            </div>`;
                } else {
                    html += `<p>${line}</p>`;
                }
            }

            if (currentSection) html += '</div>'; // Close last section

            return html;
        };

        // ENHANCED: Updated formatAnalysisTable function to include detailed analysis
        const formatEnhancedAnalysisTable = (results) => {
            if (!results) return '';

            return `COMPREHENSIVE JOB FITMENT ANALYSIS REPORT
    ========================================

    CANDIDATE OVERVIEW:
    Personal Information: 
    ${results.personalInfo}

    Educational Background: 
    ${results.education}

    Professional Experience: 
    ${results.workExperience}

    JOB REQUIREMENTS:
    ${qualifications}

    DETAILED FIT ANALYSIS:
    ${results.detailedAnalysis || 'No detailed analysis available'}

    FINAL ASSESSMENT & RECOMMENDATION:
    ${results.percentageFit}

    SUMMARY METRICS:
    - Overall Fit Score: ${extractPercentage(results.percentageFit)}
    - Recommendation Level: ${getRecommendationText(results.percentageFit)}
    - Skills Assessment: ${extractSkillsMatch(results.detailedAnalysis)}
    - Experience Level: ${extractExperienceLevel(results.detailedAnalysis)}

    Generated on: ${new Date().toLocaleString()}
    Report ID: ${Date.now()}`;
        };

        const formatQualificationMetrics = (metrics) => {
            if (!metrics) return 'No qualification metrics available';

            try {
                const skillsReport = metrics.requiredSkills && metrics.requiredSkills.length > 0
                    ? metrics.requiredSkills.map(skill =>
                        `${skill.skill}: ${skill.match ? 'MATCH' : 'MISSING'} (Required: ${skill.required ? 'Yes' : 'No'}, Candidate: ${skill.candidate ? 'Yes' : 'No'})`
                    ).join('\n')
                    : 'No skills assessed';

                const recommendationsText = metrics.recommendations && metrics.recommendations.length > 0
                    ? metrics.recommendations.map((rec, idx) => `${idx + 1}. ${rec.replace(/[✅❌⚠️💡ℹ️]/g, '').trim()}`).join('\n')
                    : 'No recommendations available';

                return `Job Qualification Assessment Report

    Overall Fit Score: ${metrics.overallFit || 0}%

    Skills Assessment:
    ${skillsReport}

    Experience:
    Required: ${metrics.experience?.required || 'Not specified'}
    Candidate: ${metrics.experience?.candidate || 'Not specified'}
    Match: ${metrics.experience?.match ? 'YES' : 'NO'}

    Education:
    Required: ${metrics.education?.required || 'Not specified'}
    Candidate: ${metrics.education?.candidate || 'Not specified'}
    Match: ${metrics.education?.match ? 'YES' : 'NO'}

    Recommendations:
    ${recommendationsText}`;
            } catch (error) {
                console.error('Error formatting qualification metrics:', error);
                return 'Error formatting qualification metrics data';
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendAdditionalPrompt();
            }
        };

        // Modal functions
        const openImageModal = (index) => {
            setCurrentImageIndex(index);
            setIsModalOpen(true);
        };

        const closeImageModal = () => {
            setIsModalOpen(false);
        };

        const nextImage = () => {
            setCurrentImageIndex((prev) => (prev + 1) % uploadedImages.length);
        };

        const prevImage = () => {
            setCurrentImageIndex((prev) => (prev - 1 + uploadedImages.length) % uploadedImages.length);
        };

        const downloadCurrentImage = () => {
            const link = document.createElement('a');
            link.href = uploadedImages[currentImageIndex];
            link.download = `resume_page_${currentImageIndex + 1}.png`;
            link.click();
        };

        const getScoreColor = (score) => {
            if (score >= 80) return 'text-green-600 dark:text-green-400';
            if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
            return 'text-red-600 dark:text-red-400';
        };

        const getScoreBgColor = (score) => {
            if (score >= 80) return 'bg-green-100 dark:bg-green-900/30';
            if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900/30';
            return 'bg-red-100 dark:bg-red-900/30';
        };

        // ENHANCED: Helper functions for enhanced analysis
        const extractPercentage = (percentageFit) => {
            if (!percentageFit) return '0%';
            const match = percentageFit.match(/(\d+)%/);
            return match ? match[0] : '0%';
        };

        const extractPercentageNumber = (percentageFit) => {
            if (!percentageFit) return 0;
            const match = percentageFit.match(/(\d+)%/);
            return match ? parseInt(match[1]) : 0;
        };

        const getProgressBarColor = (percentageFit) => {
            const percentage = extractPercentageNumber(percentageFit);
            if (percentage >= 80) return 'bg-green-500';
            if (percentage >= 60) return 'bg-yellow-500';
            if (percentage >= 40) return 'bg-orange-500';
            return 'bg-red-500';
        };

        const getRecommendationText = (percentageFit) => {
            const percentage = extractPercentageNumber(percentageFit);
            if (percentage >= 80) return 'Strong Fit';
            if (percentage >= 60) return 'Good Fit';
            if (percentage >= 40) return 'Moderate Fit';
            return 'Poor Fit';
        };

        const getRecommendationIcon = (percentageFit) => {
            const percentage = extractPercentageNumber(percentageFit);
            if (percentage >= 80) return <FaCheckCircle className="text-green-500" size={12} />;
            if (percentage >= 60) return <FaExclamationTriangle className="text-yellow-500" size={12} />;
            return <FaTimesCircle className="text-red-500" size={12} />;
        };

        const extractSkillsMatch = (detailedAnalysis) => {
            if (!detailedAnalysis) return 'N/A';
            if (detailedAnalysis.toLowerCase().includes('strong skills') || detailedAnalysis.toLowerCase().includes('excellent skills')) {
                return 'Strong';
            }
            if (detailedAnalysis.toLowerCase().includes('good skills') || detailedAnalysis.toLowerCase().includes('adequate skills')) {
                return 'Good';
            }
            if (detailedAnalysis.toLowerCase().includes('limited skills') || detailedAnalysis.toLowerCase().includes('lacking skills')) {
                return 'Limited';
            }
            return 'Mixed';
        };

        const extractExperienceLevel = (detailedAnalysis) => {
            if (!detailedAnalysis) return 'N/A';
            if (detailedAnalysis.toLowerCase().includes('senior') || detailedAnalysis.toLowerCase().includes('extensive experience')) {
                return 'Senior';
            }
            if (detailedAnalysis.toLowerCase().includes('mid-level') || detailedAnalysis.toLowerCase().includes('moderate experience')) {
                return 'Mid-Level';
            }
            if (detailedAnalysis.toLowerCase().includes('junior') || detailedAnalysis.toLowerCase().includes('entry level')) {
                return 'Junior';
            }
            return 'Varies';
        };

        // ENHANCED: Additional feature functions
        const generateInterviewQuestions = async () => {
            if (!analysisResults || !extractedText || !qualifications) {
                toast.error('Missing Data', {
                    description: 'Please complete the resume analysis first.',
                    duration: 3000
                });
                return;
            }

            setLoading(true);
            try {
                const response = await fetch('http://localhost:5000/api/resume/generate-interview-questions', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        extractedText: extractedText,
                        qualifications: qualifications,
                        analysisResults: formatEnhancedAnalysisTable(analysisResults)
                    }),
                });

                if (!response.ok) {
                    throw new Error(`Interview questions API error: ${response.status}`);
                }

                const responseData = await response.json();
                setAdditionalAnalysis(responseData.interviewQuestions);

                toast.success('Interview Questions Generated', {
                    description: 'Targeted interview questions have been generated based on the analysis.',
                    duration: 3000
                });

            } catch (error) {
                console.error("Error generating interview questions:", error);
                toast.error('Generation Failed', {
                    description: 'An error occurred while generating interview questions.',
                    duration: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        const assessSkills = async () => {
            if (!extractedText || !qualifications) {
                toast.error('Missing Data', {
                    description: 'Please complete the resume analysis first.',
                    duration: 3000
                });
                return;
            }

            setLoading(true);
            try {
                const response = await fetch('http://localhost:5000/api/resume/skills-assessment', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        extractedText: extractedText,
                        targetSkills: qualifications
                    }),
                });

                if (!response.ok) {
                    throw new Error(`Skills assessment API error: ${response.status}`);
                }

                const responseData = await response.json();
                const skillsReport = responseData.skillsAssessment.analysis || JSON.stringify(responseData.skillsAssessment, null, 2);
                setAdditionalAnalysis(skillsReport);

                toast.success('Skills Assessment Complete', {
                    description: 'Detailed skills analysis has been generated.',
                    duration: 3000
                });

            } catch (error) {
                console.error("Error during skills assessment:", error);
                toast.error('Assessment Failed', {
                    description: 'An error occurred during skills assessment.',
                    duration: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className="w-full h-full flex flex-col p-3 md:p-6 lg:p-8">
                <div className={`mb-4 transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <h1 className="text-xl font-bold text-gray-800 dark:text-white mb-1 flex items-center">
                        <FaFileImage className="mr-2" />
                        Resume Analysis
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Upload resume files for automatic text extraction and job fitment analysis.
                    </p>
                </div>

                <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md transition-all duration-500 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-yellow-500 text-white dark:text-black border-b flex items-center justify-between">
                        <h3 className="text-sm font-bold flex items-center">
                            <FaUpload className="mr-1" />
                            Resume Processing
                        </h3>
                    </div>

                    <div className="p-3">
                        <div className="mb-3">
                            <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                                Resume File (Image/PDF)
                            </label>
                            <div className="flex items-center space-x-2">
                                <div className="relative flex-grow">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/*,.pdf"
                                        className="hidden"
                                        onChange={handleFileChange}
                                    />
                                    <div className="flex items-center">
                                        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 text-xs rounded-lg w-full p-2 flex items-center">
                                            {fileName.endsWith('.pdf') ? <FaFilePdf className="mr-2 text-red-500" /> : <FaFileImage className="mr-2 text-blue-500" />}
                                            <span className="truncate">{fileName || "No file selected"}</span>
                                        </div>
                                        <button
                                            onClick={() => fileInputRef.current.click()}
                                            className="ml-2 px-3 py-2 text-xs font-medium text-white dark:text-black bg-gray-700 dark:bg-yellow-500 rounded-lg hover:bg-gray-800 dark:hover:bg-yellow-600 focus:outline-none"
                                        >
                                            Browse
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                                Job Description & Requirements
                            </label>
                            <textarea
                                value={qualifications}
                                onChange={(e) => setQualifications(e.target.value)}
                                placeholder="Enter the complete job description including required skills (e.g., JavaScript, React, 3+ years experience), education requirements, and other qualifications..."
                                rows={4}
                                className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 text-xs rounded-lg w-full p-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                💡 Tip: Include specific skills, experience levels, and education requirements for better analysis
                            </p>
                        </div>

                        <div className="text-center w-full">
                            <button
                                onClick={handleAnalyze}
                                disabled={loading}
                                className="relative inline-flex items-center justify-center overflow-hidden p-0.5 text-xs font-medium rounded-lg group bg-gradient-to-br from-black to-gray-800 dark:from-yellow-500 dark:to-yellow-600"
                            >
                                <span className="relative w-full px-3 py-2 transition-all ease-in duration-75 bg-black text-white dark:bg-transparent dark:text-black rounded-md group-hover:bg-opacity-0 group-hover:text-white dark:group-hover:text-black flex items-center justify-center">
                                    <FaEye className="mr-1" />
                                    {loading ? 'Analyzing...' : 'Start Analysis'}
                                </span>
                            </button>
                        </div>

                        {loading && (
                            <div className="mt-3">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        {ocrProgress || 'Processing resume...'}
                                    </span>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{progress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                    <div
                                        className="bg-black dark:bg-yellow-500 h-1.5 rounded-full transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        {/* Enhanced Resume Preview Section */}
                        {uploadedImages.length > 0 && (
                            <div className="mt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                        Resume Preview ({uploadedImages.length} page{uploadedImages.length > 1 ? 's' : ''})
                                    </label>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Click images to view larger
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {uploadedImages.map((imgSrc, index) => (
                                        <div
                                            key={index}
                                            className="relative group cursor-pointer bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-gray-200 dark:border-gray-600 overflow-hidden hover:border-blue-500 dark:hover:border-yellow-500 transition-all duration-200"
                                            onClick={() => openImageModal(index)}
                                        >
                                            <img
                                                src={imgSrc}
                                                alt={`Resume page ${index + 1}`}
                                                className="w-full h-40 object-contain bg-white"
                                            />

                                            {/* Overlay with expand icon */}
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                                                <FaExpand className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={20} />
                                            </div>

                                            {/* Page number badge */}
                                            <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                                                Page {index + 1}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Image Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
                        <div className="relative max-w-6xl max-h-full w-full h-full flex flex-col">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-4">
                                    <h3 className="text-white text-lg font-semibold">
                                        Resume Preview - Page {currentImageIndex + 1} of {uploadedImages.length}
                                    </h3>
                                    {uploadedImages.length > 1 && (
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={prevImage}
                                                className="text-white hover:text-yellow-400 transition-colors p-1"
                                            >
                                                <FaChevronLeft size={20} />
                                            </button>
                                            <button
                                                onClick={nextImage}
                                                className="text-white hover:text-yellow-400 transition-colors p-1"
                                            >
                                                <FaChevronRight size={20} />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={downloadCurrentImage}
                                        className="text-white hover:text-yellow-400 transition-colors p-2"
                                        title="Download Image"
                                    >
                                        <FaDownload size={16} />
                                    </button>
                                    <button
                                        onClick={closeImageModal}
                                        className="text-white hover:text-red-400 transition-colors p-2"
                                    >
                                        <FaTimes size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Image Container */}
                            <div className="flex-1 flex items-center justify-center bg-white rounded-lg overflow-hidden">
                                <img
                                    src={uploadedImages[currentImageIndex]}
                                    alt={`Resume page ${currentImageIndex + 1}`}
                                    className="max-w-full max-h-full object-contain"
                                />
                            </div>

                            {/* Navigation Buttons (for mobile) */}
                            {uploadedImages.length > 1 && (
                                <>
                                    <button
                                        onClick={prevImage}
                                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all"
                                    >
                                        <FaChevronLeft size={24} />
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all"
                                    >
                                        <FaChevronRight size={24} />
                                    </button>
                                </>
                            )}

                            {/* Page Indicators */}
                            {uploadedImages.length > 1 && (
                                <div className="flex justify-center mt-4 space-x-2">
                                    {uploadedImages.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`w-3 h-3 rounded-full transition-all ${index === currentImageIndex
                                                ? 'bg-yellow-500'
                                                : 'bg-gray-400 hover:bg-gray-300'
                                                }`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4 flex-grow" style={{ minHeight: "500px", height: "calc(100vh - 280px)" }}>
                    {/* Qualification Metrics */}
                    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md transition-all duration-500 delay-200 flex flex-col h-full ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-yellow-500 text-white dark:text-black border-b flex items-center justify-between">
                            <h3 className="text-sm font-bold flex items-center">
                                <FaChartBar className="mr-1" />
                                Qualification Metrics
                            </h3>

                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => openFullscreen('metrics')}
                                    className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                    title="Open Fullscreen"
                                >
                                    <FaExpand size={14} />
                                </button>
                                {qualificationMetrics && (
                                    <div className="relative" ref={qualificationMetricsDropdownRef}>
                                        <button
                                            onClick={() => setQualificationMetricsDropdownOpen(!qualificationMetricsDropdownOpen)}
                                            className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                        >
                                            <FaEllipsisV size={14} />
                                        </button>
                                        {qualificationMetricsDropdownOpen && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                                                <div className="py-1">
                                                    <button
                                                        onClick={() => copyToClipboard(formatQualificationMetrics(qualificationMetrics), 'Qualification Metrics')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaClipboard className="mr-2" />
                                                        Copy to Clipboard
                                                    </button>
                                                    <button
                                                        onClick={() => downloadAsWord(formatQualificationMetrics(qualificationMetrics), 'Qualification_Metrics')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaFileWord className="mr-2" />
                                                        Download as Word
                                                    </button>
                                                    <button
                                                        onClick={() => downloadAsPDF(formatQualificationMetrics(qualificationMetrics), 'Qualification_Metrics')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaFilePdf className="mr-2" />
                                                        Download as PDF
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-3 flex-grow flex flex-col">
                            {qualificationMetrics ? (
                                <div className="space-y-3 h-full overflow-y-auto">
                                    {/* Overall Score */}
                                    <div className={`p-3 rounded-lg border ${getScoreBgColor(qualificationMetrics.overallFit)}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Overall Fit Score</h4>
                                            <span className={`text-lg font-bold ${getScoreColor(qualificationMetrics.overallFit)}`}>
                                                {qualificationMetrics.overallFit}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-500 ${qualificationMetrics.overallFit >= 80 ? 'bg-green-500' :
                                                    qualificationMetrics.overallFit >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                    }`}
                                                style={{ width: `${qualificationMetrics.overallFit}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    {/* Skills Assessment */}
                                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Skills Assessment</h4>
                                        <div className="space-y-1">
                                            {qualificationMetrics.requiredSkills.map((skill, index) => (
                                                <div key={index} className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-600 dark:text-gray-400">{skill.skill}</span>
                                                    <div className="flex items-center space-x-1">
                                                        {skill.required && (
                                                            <span className="text-blue-500 text-xs">Req</span>
                                                        )}
                                                        {skill.match ? (
                                                            <FaCheckCircle className="text-green-500" size={12} />
                                                        ) : (
                                                            <FaTimesCircle className="text-red-500" size={12} />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Experience & Education */}
                                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Experience & Education</h4>
                                        <div className="space-y-2 text-xs">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Experience</span>
                                                {qualificationMetrics.experience.match ? (
                                                    <FaCheckCircle className="text-green-500" size={12} />
                                                ) : (
                                                    <FaTimesCircle className="text-red-500" size={12} />
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Education</span>
                                                {qualificationMetrics.education.match ? (
                                                    <FaCheckCircle className="text-green-500" size={12} />
                                                ) : (
                                                    <FaTimesCircle className="text-red-500" size={12} />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recommendations */}
                                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Assessment Summary</h4>
                                        <div className="space-y-1">
                                            {qualificationMetrics.recommendations.map((rec, index) => (
                                                <div key={index} className="text-xs text-gray-600 dark:text-gray-400 flex items-start">
                                                    <span className="mr-1 flex-shrink-0">{rec.charAt(0)}</span>
                                                    <span>{rec.slice(1).trim()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 text-center flex items-center justify-center h-full">
                                    Qualification metrics will appear here after processing
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ENHANCED Analysis Results */}
                    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md transition-all duration-500 delay-300 flex flex-col h-full ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-yellow-500 text-white dark:text-black border-b flex items-center justify-between">
                            <h3 className="text-sm font-bold flex items-center">
                                <FaRobot className="mr-1" />
                                Job Fitment Analysis
                            </h3>

                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => openFullscreen('analysis')}
                                    className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                    title="Open Fullscreen"
                                >
                                    <FaExpand size={14} />
                                </button>
                                {analysisResults && (
                                    <div className="relative" ref={analysisDropdownRef}>
                                        <button
                                            onClick={() => setAnalysisDropdownOpen(!analysisDropdownOpen)}
                                            className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                        >
                                            <FaEllipsisV size={14} />
                                        </button>
                                        {analysisDropdownOpen && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                                                <div className="py-1">
                                                    <button
                                                        onClick={() => copyToClipboard(formatEnhancedAnalysisTable(analysisResults), 'Job Analysis')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaClipboard className="mr-2" />
                                                        Copy to Clipboard
                                                    </button>
                                                    <button
                                                        onClick={() => downloadAsWord(formatEnhancedAnalysisTable(analysisResults), 'Job_Analysis')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaFileWord className="mr-2" />
                                                        Download as Word
                                                    </button>
                                                    <button
                                                        onClick={() => downloadAsPDF(formatEnhancedAnalysisTable(analysisResults), 'Job_Analysis')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaFilePdf className="mr-2" />
                                                        Download as PDF
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-3 flex-grow flex flex-col">
                            {analysisResults ? (
                                <div className="space-y-3 h-full overflow-y-auto">
                                    {/* Basic Information Section */}
                                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                                            <FaUser className="mr-1" />
                                            Personal Information
                                        </h4>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">{analysisResults.personalInfo}</p>
                                    </div>

                                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                                            <FaGraduationCap className="mr-1" />
                                            Educational Attainment
                                        </h4>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">{analysisResults.education}</p>
                                    </div>

                                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                                            <FaBriefcase className="mr-1" />
                                            Work Experience
                                        </h4>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">{analysisResults.workExperience}</p>
                                    </div>

                                    {/* Enhanced Detailed Analysis Section */}
                                    {analysisResults.detailedAnalysis && (
                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
                                            <h4 className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center">
                                                <FaChartBar className="mr-1" />
                                                Detailed Fit Analysis
                                                <span className="ml-auto bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 px-2 py-1 rounded-full text-xs">
                                                    Comprehensive
                                                </span>
                                            </h4>
                                            <div className="bg-white dark:bg-gray-800 rounded-md p-2 border border-blue-100 dark:border-blue-800">
                                                <div className="text-xs text-blue-700 dark:text-blue-300 max-h-48 overflow-y-auto">
                                                    <pre className="whitespace-pre-wrap font-sans leading-relaxed">
                                                        {analysisResults.detailedAnalysis}
                                                    </pre>
                                                </div>
                                            </div>

                                            {/* Quick insights extracted from detailed analysis */}
                                            <div className="mt-2 grid grid-cols-2 gap-2">
                                                <div className="bg-white dark:bg-gray-800 p-2 rounded border border-blue-100 dark:border-blue-800">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-600 dark:text-gray-400">Skills Match</span>
                                                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                                            {extractSkillsMatch(analysisResults.detailedAnalysis)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="bg-white dark:bg-gray-800 p-2 rounded border border-blue-100 dark:border-blue-800">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-600 dark:text-gray-400">Experience</span>
                                                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                                            {extractExperienceLevel(analysisResults.detailedAnalysis)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Percentage Fit with Enhanced Visualization */}
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-3 rounded-lg border border-green-200 dark:border-green-700">
                                        <h4 className="text-xs font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center">
                                            <FaCheckCircle className="mr-1" />
                                            Fit Assessment & Recommendation
                                        </h4>

                                        {/* Extract percentage from the fit result */}
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-lg font-bold text-green-800 dark:text-green-200">
                                                {extractPercentage(analysisResults.percentageFit)}
                                            </span>
                                            <div className="flex items-center space-x-1">
                                                {getRecommendationIcon(analysisResults.percentageFit)}
                                                <span className="text-xs text-green-700 dark:text-green-300">
                                                    {getRecommendationText(analysisResults.percentageFit)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Progress bar for percentage */}
                                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-2">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-1000 ${getProgressBarColor(analysisResults.percentageFit)}`}
                                                style={{ width: `${extractPercentageNumber(analysisResults.percentageFit)}%` }}
                                            ></div>
                                        </div>

                                        <div className="text-xs text-green-700 dark:text-green-300">
                                            <pre className="whitespace-pre-wrap font-sans">
                                                {analysisResults.percentageFit}
                                            </pre>
                                        </div>
                                    </div>

                                    {/* Action buttons for additional features */}
                                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Quick Actions</h4>
                                        <div className="w-full">
                                            <button
                                                onClick={() => generateInterviewQuestions()}
                                                className="text-xs bg-yellow-500 hover:bg-gray-600 text-white px-2 py-1 rounded flex items-center justify-center w-full"
                                                disabled={loading}
                                            >
                                                <FaQuestionCircle className="mr-1" size={10} />
                                                Generate Interview Questions
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 text-center flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <FaRobot className="mx-auto mb-2 text-gray-400" size={24} />
                                        <p>Job fitment analysis will appear here after processing</p>
                                        <p className="text-xs mt-1 text-gray-400">Upload a resume and enter job requirements to begin</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Additional Analysis */}
                    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md transition-all duration-500 delay-400 flex flex-col h-full ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-yellow-500 text-white dark:text-black border-b flex items-center justify-between">
                            <h3 className="text-sm font-bold flex items-center">
                                <FaQuestionCircle className="mr-1" />
                                Additional Questions
                            </h3>

                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => openFullscreen('additional')}
                                    className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                    title="Open Fullscreen"
                                >
                                    <FaExpand size={14} />
                                </button>
                                {additionalAnalysis && (
                                    <div className="relative" ref={additionalDropdownRef}>
                                        <button
                                            onClick={() => setAdditionalDropdownOpen(!additionalDropdownOpen)}
                                            className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                        >
                                            <FaEllipsisV size={14} />
                                        </button>
                                        {additionalDropdownOpen && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                                                <div className="py-1">
                                                    <button
                                                        onClick={() => copyToClipboard(additionalAnalysis, 'Additional Analysis')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaClipboard className="mr-2" />
                                                        Copy to Clipboard
                                                    </button>
                                                    <button
                                                        onClick={() => downloadAsWord(additionalAnalysis, 'Additional_Analysis')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaFileWord className="mr-2" />
                                                        Download as Word
                                                    </button>
                                                    <button
                                                        onClick={() => downloadAsPDF(additionalAnalysis, 'Additional_Analysis')}
                                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FaFilePdf className="mr-2" />
                                                        Download as PDF
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-3 flex-grow flex flex-col">
                            <div className="mb-3">
                                <textarea
                                    value={userPrompt}
                                    onChange={(e) => setUserPrompt(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Ask specific questions about the candidate..."
                                    rows={3}
                                    className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 text-xs rounded-lg w-full p-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <button
                                    onClick={sendAdditionalPrompt}
                                    disabled={loading || !userPrompt.trim()}
                                    className="mt-2 w-full relative inline-flex items-center justify-center overflow-hidden p-0.5 text-xs font-medium rounded-lg group bg-gradient-to-br from-black to-gray-800 dark:from-yellow-500 dark:to-yellow-600"
                                >
                                    <span className="relative w-full px-3 py-2 transition-all ease-in duration-75 bg-black text-white dark:bg-transparent dark:text-black rounded-md group-hover:bg-opacity-0 group-hover:text-white dark:group-hover:text-black flex items-center justify-center">
                                        <FaQuestionCircle className="mr-1" />
                                        Ask Question
                                    </span>
                                </button>
                            </div>
                            {additionalAnalysis ? (
                                <div className="relative flex-grow">
                                    <pre className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-xs text-gray-800 dark:text-gray-200 overflow-y-scroll border border-gray-200 dark:border-gray-600 whitespace-pre-wrap absolute inset-0">{additionalAnalysis}</pre>
                                </div>
                            ) : (
                                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 text-center flex items-center justify-center flex-grow">
                                    Additional analysis will appear here after asking questions
                                </div>


                            )}

                        </div>

                    </div>
                    {/* Fullscreen Modal */}
                    {fullscreenSection && (
                        <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col">
                            {/* Fullscreen Header */}
                            <div className="bg-black dark:bg-yellow-500 text-white dark:text-black px-6 py-4 flex items-center justify-between shadow-lg">
                                <h2 className="text-lg font-bold flex items-center">
                                    {fullscreenSection === 'metrics' && (
                                        <>
                                            <FaChartBar className="mr-2" />
                                            Qualification Metrics - Fullscreen View
                                        </>
                                    )}
                                    {fullscreenSection === 'analysis' && (
                                        <>
                                            <FaRobot className="mr-2" />
                                            Job Fitment Analysis - Fullscreen View
                                        </>
                                    )}
                                    {fullscreenSection === 'additional' && (
                                        <>
                                            <FaQuestionCircle className="mr-2" />
                                            Additional Questions - Fullscreen View
                                        </>
                                    )}
                                </h2>
                                <div className="flex items-center space-x-3">
                                    {/* Download options */}
                                    {fullscreenSection === 'metrics' && qualificationMetrics && (
                                        <>
                                            <button
                                                onClick={() => copyToClipboard(formatQualificationMetrics(qualificationMetrics), 'Qualification Metrics')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Copy to Clipboard"
                                            >
                                                <FaClipboard size={16} />
                                            </button>
                                            <button
                                                onClick={() => downloadAsWord(formatQualificationMetrics(qualificationMetrics), 'Qualification_Metrics')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Download as Word"
                                            >
                                                <FaFileWord size={16} />
                                            </button>
                                            <button
                                                onClick={() => downloadAsPDF(formatQualificationMetrics(qualificationMetrics), 'Qualification_Metrics')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Download as PDF"
                                            >
                                                <FaFilePdf size={16} />
                                            </button>
                                        </>
                                    )}
                                    {fullscreenSection === 'analysis' && analysisResults && (
                                        <>
                                            <button
                                                onClick={() => copyToClipboard(formatEnhancedAnalysisTable(analysisResults), 'Job Analysis')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Copy to Clipboard"
                                            >
                                                <FaClipboard size={16} />
                                            </button>
                                            <button
                                                onClick={() => downloadAsWord(formatEnhancedAnalysisTable(analysisResults), 'Job_Analysis')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Download as Word"
                                            >
                                                <FaFileWord size={16} />
                                            </button>
                                            <button
                                                onClick={() => downloadAsPDF(formatEnhancedAnalysisTable(analysisResults), 'Job_Analysis')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Download as PDF"
                                            >
                                                <FaFilePdf size={16} />
                                            </button>
                                        </>
                                    )}
                                    {fullscreenSection === 'additional' && additionalAnalysis && (
                                        <>
                                            <button
                                                onClick={() => copyToClipboard(additionalAnalysis, 'Additional Analysis')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Copy to Clipboard"
                                            >
                                                <FaClipboard size={16} />
                                            </button>
                                            <button
                                                onClick={() => downloadAsWord(additionalAnalysis, 'Additional_Analysis')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Download as Word"
                                            >
                                                <FaFileWord size={16} />
                                            </button>
                                            <button
                                                onClick={() => downloadAsPDF(additionalAnalysis, 'Additional_Analysis')}
                                                className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                                                title="Download as PDF"
                                            >
                                                <FaFilePdf size={16} />
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={closeFullscreen}
                                        className="text-white dark:text-black hover:text-red-400 transition-colors p-1"
                                        title="Exit Fullscreen (ESC)"
                                    >
                                        <FaTimes size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Fullscreen Content */}
                            <div className="flex-1 p-6 overflow-hidden">
                                {fullscreenSection === 'metrics' && (
                                    <div className="h-full overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                                        {qualificationMetrics ? (
                                            <div className="space-y-6">
                                                {/* Overall Score */}
                                                <div className={`p-6 rounded-lg border-2 ${getScoreBgColor(qualificationMetrics.overallFit)}`}>
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h4 className="text-2xl font-semibold text-gray-700 dark:text-gray-300">Overall Fit Score</h4>
                                                        <span className={`text-4xl font-bold ${getScoreColor(qualificationMetrics.overallFit)}`}>
                                                            {qualificationMetrics.overallFit}%
                                                        </span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-4">
                                                        <div
                                                            className={`h-4 rounded-full transition-all duration-500 ${qualificationMetrics.overallFit >= 80 ? 'bg-green-500' :
                                                                qualificationMetrics.overallFit >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                                }`}
                                                            style={{ width: `${qualificationMetrics.overallFit}%` }}
                                                        ></div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                    {/* Skills Assessment */}
                                                    <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                                        <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">Skills Assessment</h4>
                                                        <div className="space-y-3">
                                                            {qualificationMetrics.requiredSkills.map((skill, index) => (
                                                                <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border">
                                                                    <span className="text-gray-600 dark:text-gray-400 font-medium">{skill.skill}</span>
                                                                    <div className="flex items-center space-x-2">
                                                                        {skill.required && (
                                                                            <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-sm">Required</span>
                                                                        )}
                                                                        {skill.match ? (
                                                                            <FaCheckCircle className="text-green-500" size={18} />
                                                                        ) : (
                                                                            <FaTimesCircle className="text-red-500" size={18} />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Experience & Education */}
                                                    <div className="space-y-6">
                                                        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                                            <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">Experience & Education</h4>
                                                            <div className="space-y-4">
                                                                <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border">
                                                                    <span className="text-gray-600 dark:text-gray-400 font-medium">Experience Match</span>
                                                                    {qualificationMetrics.experience.match ? (
                                                                        <FaCheckCircle className="text-green-500" size={18} />
                                                                    ) : (
                                                                        <FaTimesCircle className="text-red-500" size={18} />
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border">
                                                                    <span className="text-gray-600 dark:text-gray-400 font-medium">Education Match</span>
                                                                    {qualificationMetrics.education.match ? (
                                                                        <FaCheckCircle className="text-green-500" size={18} />
                                                                    ) : (
                                                                        <FaTimesCircle className="text-red-500" size={18} />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Recommendations */}
                                                        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                                            <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">Assessment Summary</h4>
                                                            <div className="space-y-3">
                                                                {qualificationMetrics.recommendations.map((rec, index) => (
                                                                    <div key={index} className="p-3 bg-white dark:bg-gray-800 rounded border flex items-start">
                                                                        <span className="mr-2 flex-shrink-0 text-lg">{rec.charAt(0)}</span>
                                                                        <span className="text-gray-600 dark:text-gray-400">{rec.slice(1).trim()}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center h-full">
                                                <div className="text-center">
                                                    <FaChartBar className="mx-auto mb-4 text-gray-400" size={48} />
                                                    <p className="text-gray-500 dark:text-gray-400">No qualification metrics available</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {fullscreenSection === 'analysis' && (
                                    <div className="h-full overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                                        {analysisResults ? (
                                            <div className="space-y-6 max-w-4xl mx-auto">
                                                {/* All the analysis content with larger text and spacing */}
                                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                                    <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                                        <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                                            <FaUser className="mr-2" />
                                                            Personal Information
                                                        </h4>
                                                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{analysisResults.personalInfo}</p>
                                                    </div>

                                                    <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                                        <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                                            <FaGraduationCap className="mr-2" />
                                                            Educational Attainment
                                                        </h4>
                                                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{analysisResults.education}</p>
                                                    </div>

                                                    <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                                        <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                                                            <FaBriefcase className="mr-2" />
                                                            Work Experience
                                                        </h4>
                                                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{analysisResults.workExperience}</p>
                                                    </div>
                                                </div>

                                                {analysisResults.detailedAnalysis && (
                                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-700">
                                                        <h4 className="text-2xl font-semibold text-blue-800 dark:text-blue-300 mb-4 flex items-center">
                                                            <FaChartBar className="mr-2" />
                                                            Detailed Fit Analysis
                                                        </h4>
                                                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-blue-100 dark:border-blue-800">
                                                            <pre className="whitespace-pre-wrap font-sans leading-relaxed text-blue-700 dark:text-blue-300">
                                                                {analysisResults.detailedAnalysis}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-lg border border-green-200 dark:border-green-700">
                                                    <h4 className="text-2xl font-semibold text-green-800 dark:text-green-300 mb-4 flex items-center">
                                                        <FaCheckCircle className="mr-2" />
                                                        Fit Assessment & Recommendation
                                                    </h4>
                                                    <div className="flex items-center justify-between mb-4">
                                                        <span className="text-4xl font-bold text-green-800 dark:text-green-200">
                                                            {extractPercentage(analysisResults.percentageFit)}
                                                        </span>
                                                        <div className="flex items-center space-x-2">
                                                            {getRecommendationIcon(analysisResults.percentageFit)}
                                                            <span className="text-xl text-green-700 dark:text-green-300">
                                                                {getRecommendationText(analysisResults.percentageFit)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-4 mb-4">
                                                        <div
                                                            className={`h-4 rounded-full transition-all duration-1000 ${getProgressBarColor(analysisResults.percentageFit)}`}
                                                            style={{ width: `${extractPercentageNumber(analysisResults.percentageFit)}%` }}
                                                        ></div>
                                                    </div>
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-100 dark:border-green-800">
                                                        <pre className="whitespace-pre-wrap font-sans text-green-700 dark:text-green-300 leading-relaxed">
                                                            {analysisResults.percentageFit}
                                                        </pre>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center h-full">
                                                <div className="text-center">
                                                    <FaRobot className="mx-auto mb-4 text-gray-400" size={48} />
                                                    <p className="text-gray-500 dark:text-gray-400">No job fitment analysis available</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {fullscreenSection === 'additional' && (
                                    <div className="h-full flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-lg">
                                        {/* Question Input Area */}
                                        <div className="p-6 border-b border-gray-200 dark:border-gray-600">
                                            <h4 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">Ask Additional Questions</h4>
                                            <div className="space-y-4">
                                                <textarea
                                                    value={userPrompt}
                                                    onChange={(e) => setUserPrompt(e.target.value)}
                                                    onKeyDown={handleKeyDown}
                                                    placeholder="Ask specific questions about the candidate..."
                                                    rows={4}
                                                    className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 rounded-lg w-full p-4 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                                <button
                                                    onClick={sendAdditionalPrompt}
                                                    disabled={loading || !userPrompt.trim()}
                                                    className="relative inline-flex items-center justify-center overflow-hidden px-6 py-3 font-medium rounded-lg group bg-gradient-to-br from-black to-gray-800 dark:from-yellow-500 dark:to-yellow-600"
                                                >
                                                    <span className="relative text-white dark:text-black flex items-center">
                                                        <FaQuestionCircle className="mr-2" />
                                                        Ask Question
                                                    </span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Analysis Results */}
                                        <div className="flex-1 p-6 overflow-hidden">
                                            {additionalAnalysis ? (
                                                <div className="h-full bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6 overflow-y-auto">
                                                    <pre className="whitespace-pre-wrap font-sans text-gray-800 dark:text-gray-200 leading-relaxed">
                                                        {additionalAnalysis}
                                                    </pre>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center h-full">
                                                    <div className="text-center">
                                                        <FaQuestionCircle className="mx-auto mb-4 text-gray-400" size={48} />
                                                        <p className="text-gray-500 dark:text-gray-400">No additional analysis available</p>
                                                        <p className="text-gray-400 mt-2">Ask a question to get started</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

        );
    };



    export default ResumeAnalysis;